import {Map, Set} from 'immutable';

type Registry = Map<string, unknown>;

interface RegistryInterface {
  getInvalidationKeys: (partialQueryObject: Record<string, unknown>) => Set<string>;
  addInterestKey: (queryObject: Record<string, unknown>, irKey: string) => RegistryInterface;
  getRegistryContents: () => Registry;
}

export function getQueryRegistryInterface(
  initialRegistryContents: Registry = Map(),
  shouldRestoryRegistry: boolean = false
): RegistryInterface {
  let REGISTRY_CONTENTS = initialRegistryContents;
  if (shouldRestoryRegistry && !REGISTRY_CONTENTS.isEmpty()) {
    REGISTRY_CONTENTS = restoreRegistry(REGISTRY_CONTENTS);
  }

  return {
    getInvalidationKeys: (partialQueryObject): Set<string> => {
      if (partialQueryObject.resourcePath) {
        const root = (partialQueryObject.resourcePath as string[]).join('/');
        return Object.keys(partialQueryObject).reduce<Set<string>>(
          (result, key) => {
            if (key === 'resourcePath') {
              return result;
            }

            const invalidations = _getInvalidations({
              subTree: REGISTRY_CONTENTS.getIn([root, key], Map()),
              query: partialQueryObject[key]
            });

            return result.intersect(invalidations);
          },
          REGISTRY_CONTENTS.getIn([root, '__ALL_QUERIES'], Set<string>()) as Set<string>
        );
      } else {
        return REGISTRY_CONTENTS.keySeq().reduce<Set<string>>((result = Set<string>(), root) => {
          return result.union(
            Object.keys(partialQueryObject).reduce<Set<string>>(
              (subResult, key) => {
                return subResult.intersect(
                  _getInvalidations({
                    subTree: REGISTRY_CONTENTS.getIn([root, key], Map()),
                    query: partialQueryObject[key]
                  })
                );
              },
              REGISTRY_CONTENTS.getIn([root, '__ALL_QUERIES'], Set<string>()) as Set<string>
            )
          );
        }, Set<string>());
      }
    },
    addInterestKey: (queryObject, irKey): RegistryInterface => {
      const root = (queryObject.resourcePath as string[]).join('/');
      REGISTRY_CONTENTS = REGISTRY_CONTENTS.updateIn([root, '__ALL_QUERIES'], Set(), (allQueries) =>
        allQueries.add(irKey)
      ).update(root, (pathTree) =>
        Object.keys(queryObject).reduce<Registry>((result, key) => {
          if (key === 'resourcePath') {
            return result;
          }
          return result
            .updateIn([key, '__ALL_QUERIES'], Set(), (allQueries) => allQueries.add(irKey))
            .update(key, (subTree) =>
              _parseSubQuery({
                subTree,
                subQuery: queryObject[key],
                irKey
              })
            );
        }, pathTree as Registry)
      );
      return getQueryRegistryInterface(REGISTRY_CONTENTS);
    },
    getRegistryContents: (): Registry => REGISTRY_CONTENTS
  };
}

function _getInvalidations({subTree, query}): Set<string> {
  if (query === null) {
    return subTree.get('__ALL_QUERIES', Set());
  }
  if (typeof query !== 'object') {
    return subTree.getIn([query, '__ALL_QUERIES'], Set());
  }
  if (query instanceof RegExp) {
    return subTree
      .keySeq()
      .reduce(
        (result, key) =>
          query.test(key) ? result.union(subTree.getIn([key, '__ALL_QUERIES'])) : result,
        subTree.get('__ALL_QUERIES', Set())
      );
  }

  return Object.keys(query).reduce(
    (result, key) => {
      return result.intersect(
        _getInvalidations({
          subTree: subTree.get(key, Map()),
          query: query[key]
        })
      );
    },
    subTree.get('__ALL_QUERIES', Set())
  );
}

function _parseSubQuery({subTree, subQuery, irKey}): Set<string> {
  if (typeof subQuery !== 'object' || subQuery === null) {
    // base case
    return subTree.updateIn([subQuery, '__ALL_QUERIES'], Set(), (queries) => queries.add(irKey));
  }

  return Object.keys(subQuery).reduce(
    (result, queryKey) =>
      result.update(queryKey, Map(), (childSubTree) =>
        _parseSubQuery({
          subTree: childSubTree,
          subQuery: subQuery[queryKey],
          irKey
        })
      ),
    subTree.update('__ALL_QUERIES', Set(), (allQueries) => allQueries.add(irKey))
  );
}

function restoreRegistry(registry: Registry): Registry {
  return registry.reduce<Registry>((result = Map<string, unknown>(), val, key = '') => {
    if (key === '__ALL_QUERIES') {
      return result.set(key, (val as Set<unknown>).toSet());
    } else if (Map.isMap(val)) {
      return result.set(key, restoreRegistry(val as Registry));
    } else {
      return result.set(key, val);
    }
  }, Map<string, unknown>());
}
